import type { FC } from "react"
import React from "react"
import type { StackProps, LinkProps } from "@chakra-ui/react"
import { Stack, Skeleton, Text, Box, Tag } from "@chakra-ui/react"
import isEqual from "lodash.isequal"

import { useTallyProposalVersionHistoryQuery } from "query/graphql"
import type {
  Account,
  AccountId,
  TallyProposalVersionHistoryQuery,
} from "query/graphql"
import CardContainer from "common/components/CardContainer"
import UserIdentity from "common/components/columns/UserIdentity"
import Link from "common/components/Link"
import { ROUTES } from "common/constants/routes"
import { getTimestampFormat } from "common/helpers/date"
import { DATE_FORMATS } from "common/constants/date"
import type { ArrayElement } from "common/types/array"

type TallyProposalVersionHistory = ArrayElement<
  TallyProposalVersionHistoryQuery["proposalWithVersions"]
>

const MAX_HISTORY_VERSIONS = 10

type Props = {
  draftId: string
  originalProposalId: string
  governanceSlug: string
  governanceId: AccountId
}

const TallyProposalVersionHistory: FC<Props & StackProps> = ({
  draftId,
  governanceSlug,
  originalProposalId,
  ...stackProps
}) => {
  const { isLoading, data } = useTallyProposalVersionHistoryQuery({
    input: {
      id: originalProposalId,
    },
  })

  if (isLoading) {
    return (
      <CardContainer title="Status">
        <Stack as="section" spacing={4} {...stackProps}>
          <Stack border="gray.light" minH="sm" p={6} rounded="sm" spacing={10}>
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
            <Skeleton h="2.784375em" w="full" />
          </Stack>
        </Stack>
      </CardContainer>
    )
  }

  const tallyProposalWithVersions =
    data?.proposalWithVersions?.slice(0, MAX_HISTORY_VERSIONS) ?? []

  if (!tallyProposalWithVersions) {
    return null
  }

  // The first item in the array should be on top and the updatedAt value should be used to render
  // Then, all other proposals should be sorted by createdAt and this field should be used to render
  const firstTallyProposal = tallyProposalWithVersions[0]
  const sortedOtherTallyProposals = tallyProposalWithVersions
    ?.slice(1, MAX_HISTORY_VERSIONS)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))

  const sortedTallyProposalWithVersions = [firstTallyProposal].concat(
    sortedOtherTallyProposals,
  )

  return (
    <CardContainer isTableView pb={2} py={0} title="Version History">
      {sortedTallyProposalWithVersions.map((tallyProposal, idx) => (
        <TallyProposalHistory
          key={`tally-proposal-${tallyProposal.id}`}
          currentDraftId={draftId}
          governanceSlug={governanceSlug}
          isLatestDraft={idx === 0}
          originalProposalId={originalProposalId}
          tallyProposal={tallyProposal}
        />
      ))}
    </CardContainer>
  )
}

type TallyProposalHistoryProps = {
  tallyProposal: TallyProposalVersionHistory
  originalProposalId: string
  currentDraftId: string
  governanceSlug: string
  isLatestDraft: boolean
}
export const TallyProposalHistory: FC<
  TallyProposalHistoryProps & LinkProps
> = ({
  tallyProposal,
  originalProposalId,
  currentDraftId,
  governanceSlug,
  isLatestDraft,
  ...linkProps
}) => {
  const isCurrentVersion = isEqual(tallyProposal.id, currentDraftId)
  const displayDate = isLatestDraft
    ? getTimestampFormat(
        tallyProposal.createdAt,
        DATE_FORMATS.statusHistoryFullMonth,
      )
    : getTimestampFormat(
        tallyProposal.createdAt,
        DATE_FORMATS.statusHistoryFullMonth,
      )

  return (
    <Stack
      _hover={{ bg: "gray.100" }}
      bgColor={isCurrentVersion ? "gray.100" : ""}
      mb={1}
      py={2}
      spacing={8}
    >
      <Link
        className="no-underline"
        href={ROUTES.governance.draft.history(
          governanceSlug,
          originalProposalId,
          isLatestDraft ? "latest" : tallyProposal.id,
        )}
        {...linkProps}
      >
        <Stack px={4} spacing={2}>
          <Text fontSize="md" fontWeight="bold">
            {displayDate}
          </Text>
          {isCurrentVersion ? (
            <Box>
              <Tag
                bgColor="gray.50"
                borderColor="gray.200"
                borderRadius="md"
                color="gray.800"
              >
                Current version
              </Tag>
            </Box>
          ) : null}
          <UserIdentity
            account={tallyProposal.proposer as Account}
            avatarSize="xs"
            displayProfileLink={false}
            fontWeight="regular"
            nameWeight="regular"
            slug={governanceSlug}
          />
        </Stack>
      </Link>
    </Stack>
  )
}

export default TallyProposalVersionHistory
